import { Box } from "@mui/material";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import OverlaySpinner from "../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import TitleWithBackButton from "../../../../common/components/functional/titleWithBackButton/TitleWithBackButton";
import { isProdEnv } from "../../../../common/helpers/envHelpers";
import useIsLoading from "../../../../hooks/useIsLoading";
import { FormContainer } from "../../../customer/customerManagement/styles";
import {
  fetchEmploymentById,
  resetEmploymentAdditionalInfo,
} from "../../../employment/employmentSlice";
import { AccessRightContext } from "../../../user/AccessRightProvider";
import { ManualSalaries } from "../ManualSalaries/ManualSalaries";
import EmploymentAccums from "./accumulations/EmploymentAccums";
import { AnnualLeave } from "./AnnualLeave";
import { BasicInfo } from "./BasicInfo";
import EmploymentCancellationBar from "./EmploymentCancellationBar";
import ExpensesForEmployment from "./ExpensesForEmployment";
import { HolidayInfo } from "./HolidayInfo";
import OtherAgreedInformation from "./OtherAgreedInformation";
import SalaryRows2 from "./SalaryRows2";
import { WorkTimeInfo } from "./workTimeInfo/WorkTimeInfo";
import { useNavigate, useParams } from "react-router-dom";
import { isForbidden } from "../../../../common/helpers/isForbidden";
import TalentProfileHeader from "../../talentProfileHeader/TalentProfileHeader";
import { fetchTalent, selectTalentById } from "../../../talents/talentsSlice";
import { ExportWorkAgreementPDF } from "./exportWorkAgreement/ExportWorkAgreementPDF";
import TargetedShiftSummary from "./targetedShifts/TargetedShiftSummary";
import { SalaryType } from "../../../orders/types/salaryType";
import { ExportWorkAgreementDocument } from "./exportWorkAgreement/ExportWorkAgreementDocument";
import { WorkAgreementDocumentSection } from "./WorkAgreementDocumentSection";
import { FringeBenefits } from "./FringeBenefits/FringeBenefits";

/*
 Removed YLPE-scoped components:
  - CurrentLeaveDetermination
  - PastLeaveDetermination
  - EffectiveAHE
  - UpcomingAHE
  - InProgressAHE
  - WorktimeReductionInfo
  - EmploymentAccums
*/

const EmploymentAdditionalInfo = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { talentId, employmentId } = useParams();

  const isLoading = useIsLoading([
    "employment",
    "customerOrganizations",
    "talents",
    "tesSalaryComponents",
  ]);

  const talent = useAppSelector((state) => selectTalentById(state, talentId!!));
  const talentsSliceStatus = useAppSelector((state) => state.talents.status);
  const employment = useAppSelector((state) => state.employment.additionalInfo);

  const { canReadEmployment, canEditEmployment, isAdmin } =
    useContext(AccessRightContext);
  const canEdit = canEditEmployment(employment?.employerId);
  const canRead = canReadEmployment(employment?.employerId);
  // Admins only can change the annual leave (for now at least). Lots of support requests
  // to change annual leave.
  const canEditAnnualLeave = isAdmin;
  const isTargetedShiftsSupported =
    !employment.isShift &&
    employment.details?.wageSystem !== SalaryType.Monthly;

  useEffect(() => {
    if (employmentId) {
      dispatch(fetchEmploymentById(employmentId)).then((action) => {
        if (isForbidden(action)) {
          navigate(`/home/talents/${talentId}/employment/`);
        }
      });
    }
  }, [employmentId, canRead, talentId, dispatch, navigate]);

  useEffect(() => {
    if (talentId && !talent && talentsSliceStatus === "idle") {
      dispatch(fetchTalent({ talentId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talentId]);

  useEffect(() => {
    return () => {
      dispatch(resetEmploymentAdditionalInfo());
    };
  }, [dispatch]);

  return (
    <>
      <OverlaySpinner isLoading={isLoading} sx={{ zIndex: 10000 }} />
      <EmploymentCancellationBar isCanceled={employment?.isCanceled} />
      {talent && <TalentProfileHeader talent={talent} />}
      <Box sx={{ scrollbarWidth: "0px", maxWidth: "1500px" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <TitleWithBackButton
            title={t("talentProfile:employmentAdditionalInfo")}
            onBackClick={() =>
              navigate(`/home/talents/${talentId}/employment/`)
            }
            containerProps={{
              sx: { gap: "6px" },
            }}
          />
          {/* if employment has workAgreementDocumentId, show different button with different query */}
          {employment.workAgreementDocumentId ? (
            <ExportWorkAgreementDocument
              workAgreementDocumentId={employment.workAgreementDocumentId}
            />
          ) : (
            !employment.isShift && (
              <ExportWorkAgreementPDF
                talentId={employment.talentId}
                employmentId={employment.employmentId}
                employmentType={employment.details?.employmentType}
              />
            )
          )}
        </Box>
        <FormContainer margin="20px 0px 0px">
          <BasicInfo
            employment={employment}
            canEdit={canEdit}
            isAdmin={isAdmin}
          />
          <WorkAgreementDocumentSection />
          {isTargetedShiftsSupported && (
            <TargetedShiftSummary employment={employment} canEdit={canEdit} />
          )}
          <WorkTimeInfo />
          <HolidayInfo employment={employment} canEdit={canEdit} />
          <AnnualLeave employment={employment} canEdit={canEditAnnualLeave} />
          <SalaryRows2 canEdit={canEdit} />
          <FringeBenefits canEdit={canEdit} />
          {!isProdEnv() && (
            <OtherAgreedInformation employment={employment} canEdit={canEdit} />
          )}
          <ManualSalaries
            employmentId={employment.employmentId}
            employmentStart={employment.details?.agreedStart}
            employmentEnd={employment.details?.agreedEnd}
            employerName={employment.details?.employerName}
          />
          <EmploymentAccums
            canEdit={canEdit}
            startDate={employment.details?.agreedStart}
            endDate={employment.details?.agreedEnd}
            employmentId={employment?.employmentId}
            continuousEmploymentId={employment?.continuousEmploymentId}
          />
          <ExpensesForEmployment
            employmentId={employment?.employmentId}
            startDate={employment.details?.agreedStart}
            endDate={employment.details?.agreedEnd}
          />
        </FormContainer>
      </Box>
    </>
  );
};

export default EmploymentAdditionalInfo;
